import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { format } from 'date-fns';
import { useFincaId } from '../context/FincaContext';

/* ✅ Catálogo de tecnologías de alimentación */
const TECNOLOGIAS = [
  'AQ1',
  'Eruvaka',
  'Jetfeeder',
  'AquaExchange',
  'Marine Instrument',
];

/* ✅ Normaliza cualquier valor de geometría/array de equipos a un array real */
function normalizarArrayEquipos(valor) {
  if (!valor) return [];
  if (Array.isArray(valor)) return valor;
  if (typeof valor === 'string') {
    const t = valor.trim();
    if (t === '' || t === 'null' || t === '{}' || t === '[]') return [];
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  if (typeof valor === 'object') {
    const vals = Object.values(valor);
    return vals.filter(Boolean);
  }
  return [];
}

/* ✅ Normaliza una piscina completa (equipos + batimetría) */
function normalizarPiscina(p) {
  if (!p) return p;
  return {
    ...p,
    geometria_alimentadores: normalizarArrayEquipos(p.geometria_alimentadores),
    geometria_aireadores:    normalizarArrayEquipos(p.geometria_aireadores),
    geometria_equipos:       normalizarArrayEquipos(p.geometria_equipos),
    batimetria:              normalizarArrayEquipos(p.batimetria),
  };
}

/* ✅ Detecta si una piscina tiene geometría dibujada */
function tieneGeometria(geo) {
  if (!geo) return false;

  if (typeof geo === 'object') {
    if (geo.type && Array.isArray(geo.coordinates) && geo.coordinates.length > 0) return true;
    if (Array.isArray(geo.coordinates) && geo.coordinates.length > 0) return true;
    if (Array.isArray(geo) && geo.length > 0) return true;
    return false;
  }

  if (typeof geo === 'string') {
    const t = geo.trim();
    if (t === '' || t === 'null' || t === '{}' || t === '[]' || t === '""') return false;
    try {
      return tieneGeometria(JSON.parse(t));
    } catch {
      return false;
    }
  }

  return false;
}

export default function Piscina() {
  const fincaIdContext = useFincaId();
  const navigate = useNavigate();

  const [piscinas, setPiscinas] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [sectorFiltro, setSectorFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('activas');
  const [busqueda, setBusqueda] = useState('');
  const [editando, setEditando] = useState(null);
  const [modoNuevaCorrida, setModoNuevaCorrida] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [mostrarModal, setMostrarModal] = useState(false);

  /* ✅ Modal de equipos (alimentadores / aireadores / tolvas madres) */
  const [modalEquipos, setModalEquipos] = useState(null);

  const [form, setForm] = useState({
    codigo: '',
    area_ha: '',
    sector_id: '',
    cantidad_alimentadores: '',
    cantidad_aireadores: '',
    cantidad_equipos: '',
    tecnologia: '',
    batimetria: [],
    numero_corrida: '',
    fecha_siembra: '',
    cantidad_sembrada: '',
    peso_inicial_g: '',
  });

  useEffect(() => {
    if (fincaIdContext) {
      cargarTodo(fincaIdContext);
    } else {
      setSectores([]);
      setPiscinas([]);
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  const cargarTodo = async (idFinca) => {
    setCargando(true);
    try {
      const { data: secs } = await api.get(`/sectores/finca/${idFinca}`);
      const listaSecs = Array.isArray(secs) ? secs : [];
      setSectores(listaSecs);

      const { data: pis } = await api.get('/piscinas');
      const idsSectores = new Set(listaSecs.map((s) => s.id));
      const piscinasDeFinca = Array.isArray(pis)
        ? pis.filter((p) => idsSectores.has(p.sector_id))
        : [];
      setPiscinas(piscinasDeFinca);

      setMensaje(null);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'Error al cargar' });
    } finally {
      setCargando(false);
    }
  };

  const piscinasFiltradas = piscinas.filter((p) => {
    if (sectorFiltro && p.sector_id !== sectorFiltro) return false;
    if (estadoFiltro === 'activas' && !p.activo) return false;
    if (estadoFiltro === 'inactivas' && p.activo) return false;
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      if (!p.codigo.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const abrirEditar = (p) => {
    setEditando(p);
    setModoNuevaCorrida(false);
    setForm({
      codigo: p.codigo || '',
      area_ha: p.area_ha != null ? String(p.area_ha) : '',
      sector_id: p.sector_id || '',
      cantidad_alimentadores:
        p.cantidad_alimentadores != null ? String(p.cantidad_alimentadores) : '0',
      cantidad_aireadores:
        p.cantidad_aireadores != null ? String(p.cantidad_aireadores) : '0',
      cantidad_equipos:
        p.cantidad_equipos != null ? String(p.cantidad_equipos) : '0',
      tecnologia: p.tecnologia || '',
      batimetria: normalizarArrayEquipos(p.batimetria),
      numero_corrida: p.numero_corrida != null ? String(p.numero_corrida) : '',
      fecha_siembra: p.fecha_siembra
        ? format(new Date(p.fecha_siembra), 'yyyy-MM-dd')
        : '',
      cantidad_sembrada: p.cantidad_sembrada != null ? String(p.cantidad_sembrada) : '',
      peso_inicial_g: p.peso_inicial_g != null ? Number(p.peso_inicial_g).toFixed(2) : '',
    });
    setMostrarModal(true);
  };

  const irADibujarEnMapa = () => {
    if (!editando) return;
    const id = editando.id;
    setMostrarModal(false);
    setEditando(null);
    setModoNuevaCorrida(false);
    navigate(`/mapa?editar=${id}`);
  };

  const activarYIniciarCorrida = async (p) => {
    if (!confirm(
      `¿Activar la piscina "${p.codigo}" e iniciar una nueva corrida?\n\n` +
      `Los datos de la corrida anterior (#${p.numero_corrida || '?'}) quedarán guardados en el histórico.`
    )) return;

    setMensaje(null);
    try {
      await api.put(`/piscinas/${p.id}/activar`);
      await cargarTodo(fincaIdContext);

      setEditando({
        ...p,
        activo: true,
        siembra_id: null,
        numero_corrida: null,
        fecha_siembra: null,
        cantidad_sembrada: null,
        peso_inicial_g: null,
      });
      setModoNuevaCorrida(true);

      const siguienteCorrida = (p.numero_corrida || 0) + 1;

      setForm({
        codigo: p.codigo || '',
        area_ha: p.area_ha != null ? String(p.area_ha) : '',
        sector_id: p.sector_id || '',
        cantidad_alimentadores:
          p.cantidad_alimentadores != null ? String(p.cantidad_alimentadores) : '0',
        cantidad_aireadores:
          p.cantidad_aireadores != null ? String(p.cantidad_aireadores) : '0',
        cantidad_equipos:
          p.cantidad_equipos != null ? String(p.cantidad_equipos) : '0',
        tecnologia: p.tecnologia || '',
        batimetria: normalizarArrayEquipos(p.batimetria),
        numero_corrida: String(siguienteCorrida),
        fecha_siembra: new Date().toISOString().split('T')[0],
        cantidad_sembrada: '',
        peso_inicial_g: '',
      });
      setMostrarModal(true);

      setMensaje({
        tipo: 'ok',
        texto: `Piscina "${p.codigo}" activada. Completa los datos de la nueva corrida #${siguienteCorrida}.`,
      });
    } catch (err) {
      setMensaje({
        tipo: 'error',
        texto: err.response?.data?.error || 'Error al activar',
      });
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setMensaje(null);
    try {
      await api.put(`/piscinas/${editando.id}`, {
        codigo: form.codigo,
        area_ha: form.area_ha ? parseFloat(form.area_ha) : null,
        sector_id: form.sector_id,
        tecnologia: form.tecnologia || null,
        batimetria: form.batimetria || [],
      });

      const tieneDatosSiembra = form.fecha_siembra && form.cantidad_sembrada;

      if (tieneDatosSiembra) {
        const payloadSiembra = {
          piscina_id: editando.id,
          fecha_siembra: form.fecha_siembra,
          cantidad_sembrada: parseInt(form.cantidad_sembrada),
          peso_inicial_g: form.peso_inicial_g ? parseFloat(form.peso_inicial_g) : null,
          numero_corrida: form.numero_corrida ? parseInt(form.numero_corrida) : null,
        };

        if (modoNuevaCorrida || !editando.siembra_id) {
          await api.post('/siembras', payloadSiembra);
        } else {
          await api.put(`/siembras/${editando.siembra_id}`, payloadSiembra);
        }
      }

      setMostrarModal(false);
      setEditando(null);
      setModoNuevaCorrida(false);
      await cargarTodo(fincaIdContext);
      setMensaje({
        tipo: 'ok',
        texto: modoNuevaCorrida
          ? 'Nueva corrida registrada correctamente'
          : 'Piscina actualizada correctamente',
      });
    } catch (err) {
      setMensaje({
        tipo: 'error',
        texto: err.response?.data?.error || 'Error al guardar',
      });
    }
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setEditando(null);
    setModoNuevaCorrida(false);
  };

  /* =========================================================
     ✅ Modal de equipos (coordenadas)
     ========================================================= */
  const abrirModalEquipos = (piscina, tipo) => {
    let items = [];
    let titulo = '';
    if (tipo === 'alimentadores') {
      items = normalizarArrayEquipos(piscina.geometria_alimentadores);
      titulo = `Alimentadores — ${piscina.codigo}`;
    } else if (tipo === 'aireadores') {
      items = normalizarArrayEquipos(piscina.geometria_aireadores);
      titulo = `Aireadores — ${piscina.codigo}`;
    } else if (tipo === 'equipos') {
      items = normalizarArrayEquipos(piscina.geometria_equipos);
      titulo = `Tolvas Madres — ${piscina.codigo}`;
    }
    setModalEquipos({ piscina, tipo, items, titulo });
  };

  const cerrarModalEquipos = () => setModalEquipos(null);

  const copiarCoordenadas = (item) => {
    const texto = `${item.lat}, ${item.lng}`;
    navigator.clipboard.writeText(texto);
    setMensaje({ tipo: 'ok', texto: `📋 Coordenadas copiadas: ${texto}` });
    setTimeout(() => setMensaje(null), 2000);
  };

  const totalAlimentadores =
    (parseInt(form.cantidad_alimentadores) || 0) +
    (parseInt(form.cantidad_equipos) || 0);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <h2 className="text-xl font-semibold text-gray-700">Piscinas</h2>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Buscar:</label>
            <div className="relative">
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Ej: Ps 01"
                className="pl-8 pr-8 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm w-40"
              />
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
              {busqueda && (
                <button
                  onClick={() => setBusqueda('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xs"
                  title="Limpiar búsqueda"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Sector:</label>
            <select
              value={sectorFiltro}
              onChange={(e) => setSectorFiltro(e.target.value)}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="">Todos</option>
              {sectores.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Estado:</label>
            <select
              value={estadoFiltro}
              onChange={(e) => setEstadoFiltro(e.target.value)}
              className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="activas">Activas</option>
              <option value="inactivas">Inactivas</option>
              <option value="todas">Todas</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mb-4 p-3 bg-blue-50 text-blue-900 rounded text-sm">
        <strong>💡 Info:</strong> Cuando se registra una <strong>cosecha</strong>, la piscina
        se desactiva. Al hacer clic en <strong>🔓 Activar</strong> se abrirá el formulario
        para registrar una <strong>nueva corrida</strong>, conservando el histórico de las anteriores.
        Desde el modal de edición puedes ir al <strong>🗺️ mapa</strong> para dibujar su geometría
        y colocar los equipos (alimentadores, aireadores y Tolvas Madres).
        <br />
        <strong>📋 Tip:</strong> Haz clic en los números de <strong>Alimentadores</strong>,
        <strong>Aireadores</strong> o <strong>Tolvas Madres</strong> para ver el detalle de cada equipo
        con sus coordenadas y códigos.
      </div>

      {mensaje && (
        <div className={`mb-4 p-3 rounded ${mensaje.tipo === 'ok' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {mensaje.texto}
        </div>
      )}

      {cargando ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          Cargando...
        </div>
      ) : piscinasFiltradas.length === 0 ? (
        <div className="bg-white p-6 rounded shadow text-center text-gray-500">
          No hay piscinas en este filtro.
        </div>
      ) : (
        <div className="bg-white rounded shadow overflow-hidden">
          <div className="overflow-x-auto">
            {/* ✅ TABLA DE PISCINAS (restaurada) */}
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-3 font-semibold">Piscina</th>
                  <th className="text-center px-3 py-3 font-semibold">Estado</th>
                  <th className="text-center px-3 py-3 font-semibold">Corrida</th>
                  <th className="text-right px-3 py-3 font-semibold">Hectáreas</th>
                  <th className="text-left px-3 py-3 font-semibold">Sector</th>
                  <th className="text-center px-2 py-3 font-semibold"># Alimentadores</th>
                  <th className="text-left px-3 py-3 font-semibold">Tecnología</th>
                  <th className="text-center px-2 py-3 font-semibold"># Aireadores</th>
                  <th className="text-center px-2 py-3 font-semibold"># Tolvas Madres</th>
                  <th className="text-center px-3 py-3 font-semibold" title="Batimetría registrada">🗺️</th>
                  <th className="text-left px-3 py-3 font-semibold">Fecha siembra</th>
                  <th className="text-right px-3 py-3 font-semibold">Cant. sembrada</th>
                  <th className="text-right px-3 py-3 font-semibold">Peso siembra (g)</th>
                  <th className="text-center px-3 py-3 font-semibold" title="¿Tiene geometría dibujada en el mapa?">🗺️ Mapa</th>
                  <th className="text-right px-3 py-3 font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {piscinasFiltradas.map((p) => {
                  const alimsArr      = normalizarArrayEquipos(p.geometria_alimentadores);
                  const tolvasArr     = normalizarArrayEquipos(p.geometria_equipos);
                  const aireadoresArr = normalizarArrayEquipos(p.geometria_aireadores);
                  const batimetriaArr = normalizarArrayEquipos(p.batimetria);

                  const alimsTotal      = alimsArr.length + tolvasArr.length;
                  const tolvasTotal     = tolvasArr.length;
                  const aireadoresTotal = aireadoresArr.length;

                  return (
                    <tr
                      key={p.id}
                      className={`border-t ${!p.activo ? 'bg-gray-50 opacity-75' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-3 py-2 font-medium">
                        <div>{p.codigo}</div>
                        <div className="text-[10px] text-gray-500 font-normal">{p.sector_nombre}</div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.activo ? (
                          <span className="inline-block px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">
                            ● Activa
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-semibold">
                            ● Inactiva
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.numero_corrida != null ? (
                          <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                            #{p.numero_corrida}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600">
                        {p.area_ha != null ? Number(p.area_ha).toFixed(2) : '-'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{p.sector_nombre}</td>

                      <td className="px-3 py-2 text-center">
                        {alimsTotal > 0 ? (
                          <button
                            type="button"
                            onClick={() => abrirModalEquipos(p, 'alimentadores')}
                            className="inline-block px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-semibold cursor-pointer hover:bg-purple-200 transition"
                            title={`Clic para ver el detalle de ${alimsTotal} alimentadores`}
                          >
                            {alimsTotal}
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      <td className="px-3 py-2 text-gray-700 text-xs">
                        {p.tecnologia || <span className="text-gray-300">—</span>}
                      </td>

                      <td className="px-3 py-2 text-center">
                        {aireadoresTotal > 0 ? (
                          <button
                            type="button"
                            onClick={() => abrirModalEquipos(p, 'aireadores')}
                            className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold cursor-pointer hover:bg-blue-200 transition"
                            title={`Clic para ver el detalle de ${aireadoresTotal} aireadores`}
                          >
                            {aireadoresTotal}
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      <td className="px-3 py-2 text-center">
                        {tolvasTotal > 0 ? (
                          <button
                            type="button"
                            onClick={() => abrirModalEquipos(p, 'equipos')}
                            className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-xs font-semibold cursor-pointer hover:bg-emerald-200 transition"
                            title={`Clic para ver el detalle de ${tolvasTotal} tolvas madres`}
                          >
                            {tolvasTotal}
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>

                      <td className="px-3 py-2 text-center">
                        {batimetriaArr.length > 0 ? (
                          <span className="text-green-600 font-bold" title="Batimetría cargada">✅</span>
                        ) : (
                          <span className="text-gray-300" title="Sin batimetría">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {p.fecha_siembra ? (
                          format(new Date(p.fecha_siembra), 'dd/MM/yyyy')
                        ) : (
                          <span className="text-gray-400 text-xs">sin siembra</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.cantidad_sembrada != null
                          ? Number(p.cantidad_sembrada).toLocaleString()
                          : '-'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {p.peso_inicial_g != null
                          ? Number(p.peso_inicial_g).toFixed(2)
                          : '-'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {tieneGeometria(p.geometria) ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold"
                            title="Geometría dibujada en el mapa"
                          >
                            🗺️ Sí
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs" title="Sin geometría dibujada">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {p.activo ? (
                          <button
                            onClick={() => abrirEditar(p)}
                            className="text-blue-600 hover:underline text-xs"
                          >
                            Editar
                          </button>
                        ) : (
                          <button
                            onClick={() => activarYIniciarCorrida(p)}
                            className="px-3 py-1 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700"
                          >
                            🔓 Activar y nueva corrida
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Modal editar piscina ─── */}
      {mostrarModal && editando && (
        <div
          className="fixed inset-0 md:left-64 z-[9999] flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(75, 85, 99, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
            style={{
              border: '2px solid #0D47A1',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
          >
            <div
              className="px-4 py-2 text-white flex items-center justify-between"
              style={{
                background: modoNuevaCorrida
                  ? 'linear-gradient(to right, #2E7D32, #43A047)'
                  : 'linear-gradient(to right, #0D47A1, #1565C0)',
                borderBottom: modoNuevaCorrida
                  ? '2px solid #2E7D32'
                  : '2px solid #0D47A1',
              }}
            >
              <div className="flex items-baseline gap-2">
                <h2 className="text-sm font-bold">
                  {modoNuevaCorrida ? '🆕 Nueva corrida' : 'Editar piscina'}
                </h2>
                <span className="text-xs" style={{ color: '#E8F5E9', opacity: 0.85 }}>
                  {editando.codigo}
                </span>
              </div>
              <button
                onClick={cerrarModal}
                className="rounded-full w-7 h-7 flex items-center justify-center transition text-xs"
                style={{
                  color: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <form onSubmit={guardar} className="flex-1 overflow-y-auto">
              <div className="px-5 py-4 space-y-4">
                <div>
                  <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide text-center mb-3">
                    Datos de la piscina
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-xs text-gray-500">Código / Nombre</span>
                      <input
                        type="text"
                        value={form.codigo}
                        onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                        className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                        required
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-500">Hectáreas</span>
                      <input
                        type="number"
                        step="0.01"
                        value={form.area_ha}
                        onChange={(e) => setForm({ ...form, area_ha: e.target.value })}
                        className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                      />
                    </label>
                    <label className="block col-span-2">
                      <span className="text-xs text-gray-500">Sector</span>
                      <select
                        value={form.sector_id}
                        onChange={(e) => setForm({ ...form, sector_id: e.target.value })}
                        className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm bg-white"
                        required
                      >
                        <option value="">-- Selecciona --</option>
                        {sectores.map((s) => (
                          <option key={s.id} value={s.id}>{s.nombre}</option>
                        ))}
                      </select>
                    </label>

                    <label className="block">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        Cant. Alimentadores
                        <span className="text-[10px] text-purple-600 font-semibold">(Alim + Tolvas)</span>
                      </span>
                      <input
                        type="text"
                        value={totalAlimentadores}
                        readOnly
                        title="Suma automática: Alimentadores + Tolvas Madres. Se edita desde el mapa."
                        className="mt-1 w-full px-2.5 py-1.5 border border-purple-200 rounded-lg text-sm bg-purple-50 text-purple-900 font-bold cursor-not-allowed"
                      />
                    </label>

                    <label className="block">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        Cant. Aireadores
                        <span className="text-[10px] text-blue-600 font-semibold">(auto)</span>
                      </span>
                      <input
                        type="text"
                        value={form.cantidad_aireadores}
                        readOnly
                        title="Se edita desde el mapa."
                        className="mt-1 w-full px-2.5 py-1.5 border border-blue-200 rounded-lg text-sm bg-blue-50 text-blue-900 font-bold cursor-not-allowed"
                      />
                    </label>

                    <label className="block col-span-2">
                      <span className="text-xs text-gray-500">Tecnología de alimentación</span>
                      <select
                        value={form.tecnologia}
                        onChange={(e) => setForm({ ...form, tecnologia: e.target.value })}
                        className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm bg-white"
                      >
                        <option value="">-- Selecciona tecnología --</option>
                        {TECNOLOGIAS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </label>

                    <p className="col-span-2 text-[11px] text-gray-400 -mt-1">
                      💡 Los contadores se actualizan automáticamente al colocar equipos desde el
                      🗺️ <strong>mapa</strong>. Los nombres personalizados de cada equipo se editan en el mapa.
                    </p>
                  </div>
                </div>

                {!editando.geometria && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-800 flex items-start gap-2">
                    <span className="text-base">⚠️</span>
                    <span>
                      Esta piscina aún <strong>no tiene geometría dibujada</strong>.
                      Usa el botón <strong>🗺️ Dibujar en el mapa</strong> para trazarla.
                    </span>
                  </div>
                )}

                <div
                  className="-mx-5 px-5 py-2 mt-2 text-center"
                  style={{
                    backgroundColor: modoNuevaCorrida ? '#C8E6C9' : '#E8F5E9',
                    borderTop: '2px solid #2E7D32',
                    borderBottom: '2px solid #2E7D32',
                  }}
                >
                  <h3 className="text-xs font-bold uppercase tracking-wide" style={{ color: '#2E7D32' }}>
                    {modoNuevaCorrida ? '🆕 Nueva corrida' : 'Datos de siembra'}
                  </h3>
                </div>

                <div className="pt-1">
                  {modoNuevaCorrida && (
                    <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800 text-center">
                      Completa los datos para iniciar la nueva corrida
                    </div>
                  )}
                  {!modoNuevaCorrida && (
                    <p className="text-xs text-gray-400 mb-3 text-center">
                      Déjalo vacío si aún no ha sido sembrada.
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2 flex justify-center">
                      <label className="block w-48">
                        <span className="text-xs text-gray-500 block text-center">N° de corrida</span>
                        <input
                          type="number"
                          value={form.numero_corrida}
                          onChange={(e) => setForm({ ...form, numero_corrida: e.target.value })}
                          placeholder="1"
                          className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm text-center"
                          autoFocus={modoNuevaCorrida}
                        />
                      </label>
                    </div>
                    <label className="block">
                      <span className="text-xs text-gray-500">Fecha siembra</span>
                      <input
                        type="date"
                        value={form.fecha_siembra}
                        onChange={(e) => setForm({ ...form, fecha_siembra: e.target.value })}
                        className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                        required={modoNuevaCorrida}
                      />
                    </label>
                    <label className="block">
                      <span className="text-xs text-gray-500">Cantidad</span>
                      <input
                        type="number"
                        value={form.cantidad_sembrada}
                        onChange={(e) => setForm({ ...form, cantidad_sembrada: e.target.value })}
                        placeholder="50000"
                        className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
                        required={modoNuevaCorrida}
                      />
                    </label>
                    <div className="col-span-2 flex justify-center">
                      <label className="block w-48">
                        <span className="text-xs text-gray-500 block text-center">Peso de siembra (g)</span>
                        <input
                          type="number"
                          step="0.01"
                          value={form.peso_inicial_g}
                          onChange={(e) => setForm({ ...form, peso_inicial_g: e.target.value })}
                          placeholder="0.50"
                          className="mt-1 w-full px-2.5 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm text-center"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="px-5 py-3 flex justify-center gap-2 flex-wrap"
                style={{ backgroundColor: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}
              >
                <button
                  type="button"
                  onClick={irADibujarEnMapa}
                  className="px-4 py-1.5 bg-orange-500 text-white rounded-lg text-sm font-medium transition hover:bg-orange-600"
                  title={
                    editando.geometria
                      ? 'Ver o redibujar la geometría en el mapa'
                      : 'Dibujar la geometría de esta piscina'
                  }
                >
                  {editando.geometria ? '🗺️ Ver / redibujar en mapa' : '🗺️ Dibujar en el mapa'}
                </button>
                <button
                  type="button"
                  onClick={cerrarModal}
                  className="px-5 py-1.5 rounded-lg text-sm text-gray-700 transition hover:bg-gray-200"
                  style={{ border: '1px solid #D1D5DB' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-1.5 text-white rounded-lg text-sm font-medium transition"
                  style={{ backgroundColor: modoNuevaCorrida ? '#2E7D32' : '#0D47A1' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = modoNuevaCorrida ? '#43A047' : '#1565C0'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = modoNuevaCorrida ? '#2E7D32' : '#0D47A1'}
                >
                  {modoNuevaCorrida ? 'Iniciar nueva corrida' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal de equipos (coordenadas + códigos) ─── */}
      {modalEquipos && (
        <div
          className="fixed inset-0 md:left-64 z-[9999] flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(75, 85, 99, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={cerrarModalEquipos}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            style={{
              border: '2px solid #0D47A1',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-5 py-3 text-white flex items-center justify-between"
              style={{
                background: 'linear-gradient(to right, #0D47A1, #1565C0)',
                borderBottom: '2px solid #0D47A1',
              }}
            >
              <div>
                <h2 className="text-sm font-bold">{modalEquipos.titulo}</h2>
                <p className="text-xs" style={{ color: '#BBDEFB' }}>
                  {modalEquipos.items.length} equipo
                  {modalEquipos.items.length === 1 ? '' : 's'} registrado
                  {modalEquipos.items.length === 1 ? '' : 's'}
                </p>
              </div>
              <button
                onClick={cerrarModalEquipos}
                className="rounded-full w-7 h-7 flex items-center justify-center transition text-xs"
                style={{
                  color: 'rgba(255,255,255,0.9)',
                  border: '1px solid rgba(255,255,255,0.3)',
                }}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {modalEquipos.items.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No hay equipos registrados
                </div>
              ) : (
                /* ✅ TABLA DEL MODAL CON COLUMNAS NUEVAS */
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold w-10">#</th>
                      <th className="text-left px-3 py-2 font-semibold">Nombre</th>
                      {(modalEquipos.tipo === 'alimentadores' || modalEquipos.tipo === 'equipos') && (
                        <th className="text-left px-3 py-2 font-semibold">Tecnología</th>
                      )}
                      {modalEquipos.tipo === 'alimentadores' && (
                        <th className="text-left px-3 py-2 font-semibold">Código Alim.</th>
                      )}
                      {modalEquipos.tipo === 'equipos' && (
                        <>
                          <th className="text-left px-3 py-2 font-semibold">Código Tolva</th>
                          <th className="text-left px-3 py-2 font-semibold">Hidrofrono</th>
                        </>
                      )}
                      <th className="text-right px-3 py-2 font-semibold">Latitud</th>
                      <th className="text-right px-3 py-2 font-semibold">Longitud</th>
                      <th className="text-center px-3 py-2 font-semibold w-20">Copiar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalEquipos.items.map((item, i) => (
                      <tr
                        key={i}
                        className="border-t border-gray-100 hover:bg-blue-50/40"
                      >
                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                        <td className="px-3 py-2 font-medium">
                          {item.nombre || `Equipo #${i + 1}`}
                        </td>

                        {(modalEquipos.tipo === 'alimentadores' || modalEquipos.tipo === 'equipos') && (
                          <td className="px-3 py-2 text-xs">
                            {item.tecnologia ? (
                              <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-semibold uppercase">
                                {item.tecnologia}
                              </span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        )}

                        {modalEquipos.tipo === 'alimentadores' && (
                          <td className="px-3 py-2 text-xs font-mono">
                            {item.codigo_equipo || <span className="text-gray-300">—</span>}
                          </td>
                        )}

                        {modalEquipos.tipo === 'equipos' && (
                          <>
                            <td className="px-3 py-2 text-xs font-mono">
                              {item.codigo_tolva || <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-3 py-2 text-xs font-mono">
                              {item.codigo_hidrofrono || <span className="text-gray-300">—</span>}
                            </td>
                          </>
                        )}

                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {Number(item.lat).toFixed(6)}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {Number(item.lng).toFixed(6)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => copiarCoordenadas(item)}
                            className="text-blue-600 hover:text-blue-800 text-xs"
                            title="Copiar coordenadas"
                          >
                            📋
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div
              className="px-5 py-3 flex justify-end"
              style={{ backgroundColor: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}
            >
              <button
                type="button"
                onClick={cerrarModalEquipos}
                className="px-5 py-1.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-blue-800"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}